import { Map as NSMap } from './map';
import { xCore, xCoreHelper } from './core';
import { Overlays } from './overlays';
import { NSServerCalls } from './utils/servercalls';
import { NSResultsInterface } from './results_interface';
import { NSObjDetailsGeo } from './obj_details_geo';
import { NSObjDetails } from './obj_details';
import { URN } from './utils/urn';
import { NSPageBottompanel } from './page_bottompanel';
import { NSPageSidepanel } from './page_sidepanel';
import { extend } from 'ol/extent';
import { NSObjDetailsCloneData } from './obj_details_clone_data';
import { NSStreetView } from './street_view';

export const NSFeaturesSelect = {};

NSFeaturesSelect.init = function () {
    NSFeaturesSelectPrivate.init();

    this.f_clear = function () { return NSFeaturesSelectPrivate.f_clear.apply( NSFeaturesSelectPrivate, arguments ); };
    this.f_request_features = function () { return NSFeaturesSelectPrivate.f_request_features.apply( NSFeaturesSelectPrivate, arguments ); };
    this.f_request_features_by_click = function () { return NSFeaturesSelectPrivate.f_request_features_by_click.apply( NSFeaturesSelectPrivate, arguments ); };
    this.f_add_highlight = function () { return NSFeaturesSelectPrivate.f_add_highlight.apply( NSFeaturesSelectPrivate, arguments ); };
    this.f_add_current_selection_highlight = function () { return NSFeaturesSelectPrivate.f_add_current_selection_highlight.apply( NSFeaturesSelectPrivate, arguments ); };
    this.f_remove_highlight = function () { return NSFeaturesSelectPrivate.f_remove_highlight.apply( NSFeaturesSelectPrivate, arguments ); };
    this.f_remove_current_selection_highlight = function () { return NSFeaturesSelectPrivate.f_remove_current_selection_highlight.apply( NSFeaturesSelectPrivate, arguments ); };
    this.f_clear_highlight = function () { return NSFeaturesSelectPrivate.f_clear_highlight.apply( NSFeaturesSelectPrivate, arguments ); };
    this.f_update_layer_highlight = function () { return NSFeaturesSelectPrivate.f_update_layer_highlight.apply( NSFeaturesSelectPrivate, arguments ); };

    this.f_getHighlightedGeoJsonAndStyles = function () { return NSFeaturesSelectPrivate.f_getHighlightedGeoJsonAndStyles.apply( NSFeaturesSelectPrivate, arguments ); };
}

const NSFeaturesSelectPrivate = {
    n_total_fetures: 0,
    o_current_features: {},
    a_active_layers: [],
    o_highlight: {},
    o_current_selection: null,
    init: function () {
        this.o_highlight = new NSObjDetailsGeo.objs.GeoHighlight();
    },
    f_request_features_by_click: async function (pixel, b_incremental) {
        let a_bounding_box = this.f_get_extent_from_pixel(pixel, 5);

        const features = await NSMap.map.getFeaturesAtPixel(pixel);

        for (const feature of features) {
            const geometry = feature.getGeometry();

            if (feature.getId() === undefined || geometry.getType() !== 'Point') continue;

            const featurePixel = NSMap.map.getPixelFromCoordinate(geometry.getCoordinates());
            const featureExtent = this.f_get_extent_from_pixel(featurePixel, 2);

            a_bounding_box = extend(featureExtent, a_bounding_box);
        }

        await this.f_request_features(a_bounding_box, b_incremental, true);
    },
    f_request_features: async function (a_bounding_box, b_incremental, b_is_click) {
        try {
            const o_active_layers = await this.f_update_active_layers();

            if (o_active_layers.size === 0) return this.f_handle_response([], false, true);

            const f_callback = (res) => {
                this.f_handle_response(res.features, b_incremental, b_is_click);
            };

            let a_unique_active_layers_unfiltered = Array.from(o_active_layers);

            // in case it is present, remove street_view/vistas_rua geoserver layer
            const a_unique_active_layers = a_unique_active_layers_unfiltered.filter(geo_server_layer_name => geo_server_layer_name !== NSStreetView.f_get_geoserver_layer_name());
            
            if (a_unique_active_layers.length === 0) return this.f_handle_response([], false, true);

            const s_cql_filter = this.f_generate_request_filter(a_unique_active_layers, a_bounding_box);

            NSServerCalls.geoserver.wfs.get_feature(a_unique_active_layers.join(), 1000, null, null, null, f_callback, null, null, s_cql_filter);
        } catch (err) {
            console.log('Failed to request features');
        }
    },
    f_handle_response: function (res, incremental, b_is_click) {
        try {
            if (!incremental) this.f_clear_highlight();

            if (incremental && this.o_current_selection) this.f_restore_current_selection_highlight();

            const o_tables_features = incremental ? this.o_current_features : {};
            const o_layer_name_map = {};

            for (const active_layer of this.a_active_layers) {
                const { urn, layer_name, icon } = active_layer;
                o_layer_name_map[layer_name] = o_layer_name_map[layer_name] || { urn, icon };
            }

            res.forEach((o_feature) => {
                const [s_layer_name, s_record_id] = o_feature.id.split('.');
                const { urn, icon } = o_layer_name_map[s_layer_name];
                const features = o_tables_features[urn] || (o_tables_features[urn] = []);
                const feature_urn = URN.f_create_record_urn_from_table_urn(urn, s_record_id);

                o_feature.urn = feature_urn;

                let n_feature_index = -1;
                const b_is_included = features.filter((el, i) => (el.urn === feature_urn ? (n_feature_index = i + 1) : false)).length > 0;

                if (!incremental && b_is_included) return;

                if (incremental) {
                    if (!b_is_click && b_is_included) return;

                    if (b_is_click && b_is_included) {
                        if (res.length > 1) return;

                        features.splice(n_feature_index - 1, 1);
                        this.f_remove_highlight(feature_urn);
                        return;
                    }
                }

                features.push(o_feature);

                const o_geo_in = new NSObjDetailsGeo.objs.AddFromGeojsonGeometryIn({
                    s_guid: feature_urn,
                    urn: feature_urn,
                    o_geojson_feature_geometry: o_feature.geometry,
                    style_simple: new NSObjDetailsGeo.objs.GeoHighlightStyleSimpleIn({
                        b_add_displacement_to_highlight_an_icon: 
                            xCoreHelper.f_use_icon_auto_vetical_displacement() && icon !== undefined,
                        a_icon_size_xy: icon,
                        color: 'rgba(255, 0, 0, 0.4)'
                    }),
                });

                this.f_add_highlight(o_geo_in);
            });

            const a_table = [];
            const store_data_parent_urn = NSObjDetailsCloneData.f_get_store_data_parent_urn();
            let total_count = 0;
            let b_enable_paste = false;

            for (const urn in o_tables_features) {
                const o_tables_feature = o_tables_features[urn];

                if (store_data_parent_urn && store_data_parent_urn === urn) b_enable_paste = true;

                a_table.push({
                    s_table_urn: urn,
                    a_geojson_feature: o_tables_feature,
                });

                total_count += o_tables_feature.length;
            }

            const n_previous_total_features = this.n_total_fetures;

            this.o_current_features = o_tables_features;
            this.n_total_fetures = total_count;
            this.o_current_selection = null;

            if (total_count === n_previous_total_features && incremental) return;

            if (total_count === 0 && b_is_click) {
                NSObjDetails.f_hide_read_and_edit_section();
                NSPageSidepanel.f_hide_sidepanel();
                NSResultsInterface.f_clean_results_interface();
                NSPageBottompanel.f_hide_bottompanel();

                return;
            }

            if (total_count === 0 && n_previous_total_features > 0 && incremental) {
                NSResultsInterface.f_clean_results_interface();
                NSPageBottompanel.f_hide_bottompanel();

                return;
            }

            if (total_count === 1 && (n_previous_total_features === 0 || !incremental)) {
                NSResultsInterface.f_clean_results_interface();
                NSPageBottompanel.f_hide_bottompanel();

                this.f_open_feature_details(res[0]);

                return;
            }

            NSObjDetails.f_hide_read_and_edit_section();
            NSPageSidepanel.f_hide_sidepanel();

            const o_in = {
                a_table: a_table,
                b_enable_export: true,
                b_use_pagination: false,
                b_get_more_results_response: false,
                b_enable_paste: b_enable_paste,
                i_feature_limit: 10,
                i_feature_total_count: total_count,
                i_feature_offset: 0,
            };

            NSResultsInterface.f_reset_results_interface(o_in);
        } catch (err) {
            console.log('Failed to read response');
        }
    },
    f_get_extent_from_pixel: function (pixel, offset) {
        const a_sw = NSMap.map.getCoordinateFromPixel([pixel[0] - offset, pixel[1] + offset]);
        const a_ne = NSMap.map.getCoordinateFromPixel([pixel[0] + offset, pixel[1] - offset]);
        return [...a_sw, ...a_ne];
    },
    f_generate_polygon_coordinates: function (a_bounding_box) {
        return [
            [a_bounding_box[0], a_bounding_box[1]],
            [a_bounding_box[0], a_bounding_box[3]],
            [a_bounding_box[2], a_bounding_box[3]],
            [a_bounding_box[2], a_bounding_box[1]],
            [a_bounding_box[0], a_bounding_box[1]],
        ];
    },
    f_generate_request_filter: function (a_unique_active_layers, a_bounding_box) {
        const s_polygon_coordinates_filter = this.f_generate_polygon_coordinates(a_bounding_box).map((el) => el.join(' '));
        const s_filter_template = `INTERSECTS(__GEOM__,POLYGON((${s_polygon_coordinates_filter})))`;
        const a_filters = [];

        a_unique_active_layers.map((el) => {
            const [layer_config] = this.a_active_layers.filter((layer) => layer.layer_name === el);
            const { geometry_urn } = layer_config;

            a_filters.push(s_filter_template.replace('__GEOM__', geometry_urn));
        });

        return a_filters.join(';');
    },
    f_add_highlight: function (o_geo_in) {
        this.o_highlight.f_add_from_geojson_geometry(o_geo_in);
    },
    f_add_current_selection_highlight: function ({ urn, geometry, parent }) {
        const s_table_urn = URN.f_create_table_urn_from_record_urn(urn);

        const o_layer_config = this.f_get_layer_config({ urn: parent || s_table_urn }) || {};

        const o_geo_in = new NSObjDetailsGeo.objs.AddFromGeojsonGeometryIn({
            s_guid: 'current_feature_highlight',
            urn: parent !== undefined ? `${urn}__PARENT__${parent}` : urn,
            o_geojson_feature_geometry: geometry,
            style_simple: new NSObjDetailsGeo.objs.GeoHighlightStyleSimpleIn({
                b_add_displacement_to_highlight_an_icon: 
                    xCoreHelper.f_use_icon_auto_vetical_displacement() && o_layer_config.icon !== undefined,
                a_icon_size_xy: o_layer_config.icon,
            }),
        });

        this.f_add_highlight(o_geo_in);
    },
    f_remove_highlight: function (s_guid) {
        this.o_highlight.f_remove(s_guid);
    },
    f_remove_current_selection_highlight: function () {
        NSFeaturesSelect.f_remove_highlight('current_feature_highlight');
    },
    f_clear_highlight: function () {
        this.o_highlight.f_remove_all();
    },
    f_restore_current_selection_highlight: function () {
        const { id, urn, geometry } = this.o_current_selection;
        const [s_layer_name] = id.split('.');
        const { icon } = this.f_get_layer_config({ layer_name: s_layer_name });

        this.f_add_highlight(
            new NSObjDetailsGeo.objs.AddFromGeojsonGeometryIn({
                s_guid: urn,
                urn: urn,
                o_geojson_feature_geometry: geometry,
                style_simple: new NSObjDetailsGeo.objs.GeoHighlightStyleSimpleIn({
                    b_add_displacement_to_highlight_an_icon: 
                        xCoreHelper.f_use_icon_auto_vetical_displacement() && icon !== undefined,
                    a_icon_size_xy: icon,
                    color: 'rgba(255, 0, 0, 0.4)'
                }),
            })
        );

        this.o_current_selection = null;
    },
    f_clear: function () {
        this.n_total_fetures = 0;
        this.a_active_layers = [];
        this.o_current_features = {};
        this.o_highlight.f_remove_all();
        this.o_current_selection = null;
    },
    f_open_feature_details: function (o_feature) {
        const { urn, id } = o_feature;
        const [s_layer_name] = id.split('.');
        const o_layer_config = this.f_get_layer_config({ layer_name: s_layer_name });
        const a_icon_size_xy = o_layer_config.icon;
        const b_run_normal_map_click_obj_event = NSMap.f_b_run_normal_map_click_obj_event();
        const b_clean_join_list = true;

        if (b_run_normal_map_click_obj_event) {
            const o_in = {
                tree_node_id: urn,
                s_urn: urn,
                b_go_to: true,
                o_geo_json: o_feature,
                b_clean_join_list: b_clean_join_list,
                b_is_icon: a_icon_size_xy !== undefined,
                a_icon_size_xy: a_icon_size_xy,
            };

            NSObjDetails.f_open_details_by_feature(o_in);
        }

        NSMap.f_map_click_process_complementary_actions(urn);

        this.f_remove_highlight(urn);

        this.o_current_selection = o_feature;
    },
    f_get_layer_config: function ({ urn, layer_name }) {
        for (const active_layer of this.a_active_layers) {
            if (urn === undefined && layer_name === active_layer.layer_name) return active_layer;
            if (urn === active_layer.urn && layer_name === undefined) return active_layer;
            if (urn === active_layer.urn && layer_name === active_layer.layer_name) return active_layer;
        }
    },
    f_update_active_layers: async function () {
        const o_active_layers = new Set();
        const a_active_layers = [];
        const a_active_overlays_sorted = Overlays.f_get_all_active_overlays().sort((el1, el2) => {
            if (el1.tree_node.type === 'vector' && el2.tree_node.type !== 'vector') return -1;
            if (el1.tree_node.type !== 'vector' && el2.tree_node.type === 'vector') return 1;
            return 0;
        });

        for (const el of a_active_overlays_sorted) {
            const o_node = el.tree_node;
            const s_layer_name = o_node.geo_server_layer_name || o_node.urn.split('__SEP__').slice(-1)[0];

            if (o_node.urn != '') {
                if (o_node.icon && !o_node.icon_size) {
                    o_node.icon_size = await this.f_get_icon_dimensions(o_node.icon);
                }
    
                o_active_layers.add(s_layer_name);
                a_active_layers.push({
                    urn: o_node.urn,
                    icon: o_node.icon_size,
                    layer_name: s_layer_name,
                    geometry_urn: o_node.geometry_urn || 'geom',
                });
            }
            
        }

        this.a_active_layers = a_active_layers;

        return o_active_layers;
    },
    f_update_current_feature_highlight: function (s_urn, o_layer_config) {
        const o_current_feature_highlight = this.o_highlight.f_get('current_feature_highlight');

        if (!o_current_feature_highlight) return;

        const [s_feature_urn, s_parent_urn] = o_current_feature_highlight.urn.split('__PARENT__');

        const s_table_urn = s_parent_urn || URN.f_create_table_urn_from_record_urn(s_feature_urn);

        if (s_table_urn !== s_urn || !o_layer_config) return;

        const { urn, vector } = o_current_feature_highlight;
        const geometry = {
            coordinates: vector.getGeometry().getCoordinates(),
            type: vector.getGeometry().getType(),
        };

        const o_geo_in = new NSObjDetailsGeo.objs.AddFromGeojsonGeometryIn({
            s_guid: 'current_feature_highlight',
            urn: urn,
            o_geojson_feature_geometry: geometry,
            style_simple: new NSObjDetailsGeo.objs.GeoHighlightStyleSimpleIn({
                b_add_displacement_to_highlight_an_icon: 
                    xCoreHelper.f_use_icon_auto_vetical_displacement() && o_layer_config.icon !== undefined,
                a_icon_size_xy: o_layer_config.icon,
            }),
        });

        this.f_add_highlight(o_geo_in);
    },
    f_update_layer_highlight: async function (s_urn) {
        await this.f_update_active_layers();

        const [o_layer_config] = this.a_active_layers.filter((el) => el.urn === s_urn);

        if (this.o_current_features[s_urn] !== undefined) {
            this.o_current_features[s_urn].forEach((feature) => {
                if (!o_layer_config) return this.o_highlight.f_remove(feature.urn);

                if (this.f_get_selection_count() === 1 && this.o_highlight.f_get(feature.urn) === null) return;

                const o_geo_in = new NSObjDetailsGeo.objs.AddFromGeojsonGeometryIn({
                    s_guid: feature.urn,
                    urn: feature.urn,
                    o_geojson_feature_geometry: feature.geometry,
                    style_simple: new NSObjDetailsGeo.objs.GeoHighlightStyleSimpleIn({
                        b_add_displacement_to_highlight_an_icon: 
                            xCoreHelper.f_use_icon_auto_vetical_displacement() && o_layer_config.icon !== undefined,
                        a_icon_size_xy: o_layer_config.icon,
                        color: 'rgba(255, 0, 0, 0.4)'
                    }),
                });

                this.f_add_highlight(o_geo_in);
            });
        }

        this.f_update_current_feature_highlight(s_urn, o_layer_config);

        if (!o_layer_config && this.o_current_features[s_urn]) delete this.o_current_features[s_urn];
    },
    f_get_icon_dimensions: function (s_icon) {
        return new Promise((resolve, reject) => {
            const img = new Image();

            img.onload = () => resolve([img.width, img.height]);

            img.onerror = (error) => reject(error);

            img.src = `${xCore.application.layer_images_base_path}/${s_icon}`;
        });
    },
    f_get_selection_count: function () {
        let count = 0;

        for (let a_features of Object.values(this.o_current_features)) {
            count = +a_features.length;
        }

        return count;
    },
    f_getHighlightedGeoJsonAndStyles: function() {
        return this.o_highlight.f_getGeoJsonAndStyles();
    }
};


window.NSFeaturesSelectPrivate_ = NSFeaturesSelectPrivate;